import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { 
    Save, ArrowLeft, Trash2, Paperclip, Plus, MapPin, 
    ChevronLeft, ChevronRight, Type, Minus, User, Contact as ContactIcon, Lock, Users, Info, Search
} from 'lucide-react';
import UniversalLookup from './UniversalLookup';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import AttachmentsModal from './AttachmentsModal';
import SmartPrintButton from './PrintButton';

const PersonDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isNew = !id || id === 'new';

    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const fontContext = useFontSize();
    const fontSize = fontContext?.fontSize || 13;

    const { canCreate, canUpdate, canDelete } = usePermissions('persons');
    const isEditable = isNew ? canCreate : canUpdate;

    const [activeTab, setActiveTab] = useState('PROFILE');
    const [lookup, setLookup] = useState({ isOpen: false, field: '', type: '', targetIdx: null });
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);
    const [count, setCount] = useState(0); 
    const [loading, setLoading] = useState(false);
    const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

    // 🎯 حالة اختيار سطر جهة الاتصال (Master-Detail)
    const [selectedContactIndex, setSelectedContactIndex] = useState(null);

    const session = JSON.parse(localStorage.getItem('user') || '{}');
    
    const [data, setData] = useState({
        personid: '', displayname: '', firstname: '', lastname: '',
        status: 'ACTIVE', title: '', department: '', location: '',
        contacts: [] 
    });

    const loadData = useCallback(async () => {
        if (isNew) return;
        try {
            const res = await axios.get(`/api/person/${id}`);
            const fetchedContacts = res.data.contacts || [];
            setData({ ...res.data, contacts: fetchedContacts });
            // اختيار أول جهة اتصال تلقائياً إذا كانت موجودة
            if (fetchedContacts.length > 0) setSelectedContactIndex(0);
        } catch (err) { toast.error(t('common.load_fail', 'Failed to load data')); }
    }, [id, isNew, t]);

    useEffect(() => { loadData(); }, [loadData]);

    const loadNavigation = useCallback(async () => {
        if (isNew) return;
        const searchParams = new URLSearchParams(location.search); 
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`/api/person/${id}/navigation`,{
                params: {
                    all_sites: searchParams.get('all_sites') || "false",
                    user_siteid: session.site_id, orgid: session.org_id,
                    filter_siteid: searchParams.get('siteid') || "", 
                    personid: searchParams.get('personid') || "",
                    displayname: searchParams.get('displayname') || "",
                    status: searchParams.get('status') || "",
                    sortBy: searchParams.get('sortBy') || "personid",
                    sortDir: searchParams.get('sortDir') || "asc"
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setNav(res.data || { first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });
        } catch (err) { setNav({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 }); }
    }, [id, isNew, location.search, session.site_id, session.org_id]);

    useEffect(() => { loadNavigation(); }, [loadNavigation]);

    const fetchCount = useCallback(async () => {
        if (!data.personid || isNew) { setCount(0); return; }
        try {
            const res = await axios.get(`/api/attachments/count/PERSON/${data.personid}`);
            setCount(res.data.count || 0);
        } catch (err) { console.error("Error fetching attachment count:", err); }
    }, [data.personid, isNew]);

    useEffect(() => { fetchCount(); }, [fetchCount]);

    const handleSave = async () => {
        if (!isEditable) return;
        try {
            setLoading(true);
            await axios.post('/api/person/save', data, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
            });
            toast.success(t('common.save_success', 'Saved Successfully'));
            if (isNew) navigate(`/persons/${data.personid}`);
        } catch (err) { toast.error(err.response?.data?.detail || t('common.save_fail', 'Save Failed')); }
        finally { setLoading(false); }
    };

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: t('common.are_you_sure', 'Are you sure?'), text: t('common.cannot_be_undone', 'This action cannot be undone!'),
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
            cancelButtonColor: '#1a3353', confirmButtonText: t('common.yes', 'Yes')
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(`/api/person/${data.personid}`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                toast.success(t('common.delete_success', 'Deleted Successfully'));
                navigate('/persons'); 
            } catch (err) { toast.error(err.response?.data?.detail || t('common.delete_fail', 'Delete Failed')); }
        }
    };

    // --- 🎯 وظائف الـ Contacts الجديدة ---
    const addContact = () => {
        const newContacts = [...data.contacts, { 
            contact_name: '', position: '', email: '', phone: '', 
            mobile: '', personal_email: '', address: '', city: '', country: '', is_primary: false 
        }];
        setData({ ...data, contacts: newContacts });
        setSelectedContactIndex(newContacts.length - 1);
    };

    const removeContact = (index, e) => {
        e.stopPropagation();
        const newList = [...data.contacts];
        newList.splice(index, 1);
        setData({ ...data, contacts: newList });
        
        // تعديل الـ Index المختار بعد الحذف
        if (selectedContactIndex === index) {
            setSelectedContactIndex(null);
        } else if (selectedContactIndex > index) {
            setSelectedContactIndex(selectedContactIndex - 1);
        }
    };

    const updateContact = (index, field, value) => {
        const newList = [...data.contacts];
        if (field === 'is_primary' && value === true) {
            newList.forEach(c => c.is_primary = false); // علامة أساسية واحدة فقط
        }
        newList[index][field] = value;
        setData({ ...data, contacts: newList });
    };

    const handleSearch = (fieldName, title, defaultType, lineIdx = null, domainId = '') => {
        let extraFilters = { site_id: session.site_id, org_id: session.org_id };
        setLookup({ 
            isOpen: true, 
            title: title, 
            type: defaultType, 
            field: fieldName, 
            filters: extraFilters, 
            targetIdx: lineIdx,
            domainId: domainId 
        });
    };

    useEffect(() => {
        const triggerSave = () => handleSave();
        window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
        return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    }, [handleSave]);

    useEffect(() => {
        const triggerDelete = () => handleDelete();
        window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
        return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    }, [handleDelete]);

    const goTo = (target) => target && navigate(`/persons/${target}${location.search}`);

    return (
        <div className={`flex flex-col h-screen bg-[#f3f4f6] text-[11px] uppercase tracking-tighter ${isRTL ? 'font-cairo dir-rtl text-right' : 'font-black text-left'}`} style={{ fontSize: `${fontSize}px` }}>
            <UniversalLookup {...lookup} isOpen={lookup.isOpen} 
                onClose={() => setLookup({...lookup, isOpen:false})}
                onSelect={(row) => {
                    const val = row.location_id || row.value || row.id;
                    if (lookup.targetIdx !== null && activeTab === 'CONTACT') {
                        updateContact(lookup.targetIdx, lookup.field, val);
                    } else {
                        setData({...data, [lookup.field]: val});
                    }
                    setLookup({...lookup, isOpen:false});
                }}
            />  

            {!isNew && (
                <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false);fetchCount();}} ownertable="PERSON" ownerid={data.personid} />
            )}

            {/* --- TOP TOOLBAR --- */}
            <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg z-30 px-4 shrink-0">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/persons')} className="p-1.5 hover:bg-white/10 rounded-full transition-colors text-blue-200 active:scale-90">
                        <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
                    </button>
                    {!isNew && (
                        <div className="flex items-center bg-black/20 border border-white/10 rounded-md px-1 py-0.5 gap-1">
                            <button disabled={!nav.prev} onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
                            <button disabled={!nav.prev} onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                            <div className="px-2 text-[10px] font-mono border-x border-white/10 mx-1"><span className="text-orange-400">{nav.current_pos}</span> / {nav.total}</div>
                            <button disabled={!nav.next} onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                            <button disabled={!nav.next} onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-30"><ChevronRight size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {isEditable && (
                    <button onClick={handleSave} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded-sm flex items-center gap-2 text-[11px] transition-all shadow-md active:scale-95 uppercase">
                        <Save size={14} /> {loading ? t('common.saving', 'SAVING...') : t('common.save', 'SAVE')}
                    </button>
                    )}
                    
                    <div className="h-6 w-[1px] bg-white/10 mx-1"></div>
                    <SmartPrintButton moduleName="PERSON" refId={data.personid} />
                    <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 hover:bg-white/10 rounded text-blue-200 transition-colors" title={t('common.attachments', 'Attachments')}>
                        <Paperclip size={16} />
                        {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{count}</span>}
                    </button>
                    {!isNew && canDelete && (
                        <button onClick={handleDelete} className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors" title={t('common.delete', 'Delete')}>
                            <Trash2 size={16}/>
                        </button>
                    )}
                    
                    <div className={`flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/20 shadow-inner ${isRTL ? 'mr-4 flex-row-reverse' : 'ml-4'}`}>
                        <button onClick={() => fontContext?.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
                        <div className="flex items-center gap-1 min-w-[40px] justify-center text-blue-200">
                            <Type size={14} /><span className="font-black text-[10px]">{fontSize}</span>
                        </div>
                        <button onClick={() => fontContext?.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
                    </div>
                </div>
            </div>

            {/* --- DOCUMENT HEADER --- */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-12 shadow-sm z-20 shrink-0">
                <HeaderField label={t('persons.tables.personId', 'Person ID')} value={data.personid} onChange={v => setData({...data, personid: v})} isBold width="w-32" disabled={!isNew} isRTL={isRTL} />
                <HeaderField label={t('persons.tables.name', 'Display Name')} value={data.displayname} onChange={v => setData({...data, displayname: v})} width="w-[300px]" disabled={!isEditable} placeholder={t('common.enterDesc', 'Enter Name')} isRTL={isRTL} />
                <div className={`flex flex-col px-4 border-gray-200 shrink-0 ${isRTL ? 'border-l text-right' : 'border-r text-left'}`}>
                    <label className="text-[0.8em] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic leading-none">{t('persons.fields.location', 'Location')}</label>
                    <input className="w-32 border-b border-transparent outline-none py-0.5 font-black uppercase text-blue-700 text-[12px] bg-transparent cursor-pointer hover:border-blue-300" 
                           value={data.location} disabled={!isEditable} onClick={() => setLookup({ isOpen: true, title: t('common.search', 'Search'), type: 'locations', field: 'location' })} placeholder={t('common.search', 'Search')} readOnly />
                </div>
            </div>

            {/* --- TABS --- */}
            <div className="flex bg-white border-b border-gray-100 px-4 shrink-0">
                {[
                    { id: 'PROFILE', icon: User, label: t('persons.tabs.profile', 'Profile') }, 
                    { id: 'CONTACT', icon: ContactIcon, label: t('persons.tabs.contact', 'Contacts') },
                    { id: 'SYSTEM_ACCESS', icon: Lock, label: t('persons.tabs.access', 'System Access') }
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)} 
                        className={`px-8 py-3 flex items-center gap-2 transition-all text-[10px] font-black uppercase tracking-tighter
                        ${activeTab === tab.id ? 'border-b-2 border-[#1a3353] text-[#1a3353] bg-gray-50' : 'text-gray-400 hover:text-gray-600'} `}>
                        <tab.icon size={13}/> {tab.label}
                    </button>
                ))}
            </div>

            {/* --- CONTENT AREA --- */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#f8f9fb]">
                <div className="max-w-6xl mx-auto animate-in fade-in duration-500 pb-20">
                    
                    {activeTab === 'PROFILE' && (
                        <div className="grid grid-cols-3 gap-6">
                            <div className="col-span-2">
                                <Section title={t('persons.sections.personalInfo', 'Personal Information')} isRTL={isRTL}>
                                    <div className="grid grid-cols-2 gap-6">
                                        <EditableField label={t('persons.fields.firstName', 'First Name')} value={data.firstname} onChange={v => setData({...data, firstname: v})} disabled={!isEditable} isRTL={isRTL} />
                                        <EditableField label={t('persons.fields.lastName', 'Last Name')} value={data.lastname} onChange={v => setData({...data, lastname: v})} disabled={!isEditable} isRTL={isRTL} />
                                        <EditableField label={t('persons.fields.title', 'Job Title')} value={data.title} onChange={v => setData({...data, title: v})} disabled={!isEditable} isRTL={isRTL} />
                                        <EditableField label={t('persons.fields.department', 'Department')} value={data.department} onChange={v => setData({...data, department: v})} disabled={!isEditable} isRTL={isRTL} />
                                    </div>
                                </Section>
                            </div>
                            <div className="col-span-1">
                                <Section title={t('persons.sections.statusControl', 'Status & Control')} isRTL={isRTL}>
                                    <div className="space-y-4">
                                        <div className="p-3 bg-blue-50 rounded border border-blue-100 flex justify-between items-center">
                                            <span className="text-blue-900 font-black">{t('persons.fields.currentStatus', 'Status')}</span>
                                            <select className={`bg-transparent outline-none font-black text-[12px] cursor-pointer ${data.status === 'ACTIVE' ? 'text-green-700' : 'text-red-700'}`}
                                                    value={data.status} onChange={e => setData({...data, status: e.target.value})} disabled={!isEditable}>
                                                <option value="ACTIVE" className="text-green-700">ACTIVE</option>
                                                <option value="INACTIVE" className="text-red-700">INACTIVE</option>
                                            </select>
                                        </div>
                                    </div>
                                </Section>
                            </div>
                        </div>
                    )}

                    {activeTab === 'CONTACT' && (
                        <div className="flex flex-col gap-6 animate-in slide-in-from-right duration-500 h-full">
                            {/* Master Table */}
                            <div className="bg-white border rounded shadow-lg overflow-hidden shrink-0">
                                <div className="p-2 bg-slate-50 border-b flex justify-between items-center">
                                    <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest italic flex items-center gap-2">
                                        <Users size={14}/> {t('persons.sections.contactsDirectory', 'Master Contact List')}
                                    </span>
                                    <button onClick={addContact} disabled={!isEditable} className="bg-blue-900 text-white px-3 py-1 rounded-sm text-[9px] font-black flex items-center gap-1 hover:bg-blue-800 transition-all disabled:opacity-50">
                                        <Plus size={12}/> {t('persons.actions.addContact', 'ADD CONTACT')}
                                    </button>
                                </div>
                                <div className="max-h-[250px] overflow-y-auto custom-scrollbar">
                                    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                                        <thead className="bg-gray-50 border-b text-[9px] font-black uppercase text-gray-400 sticky top-0 z-10">
                                            <tr>
                                                <th className={`p-3 w-10 text-center ${isRTL ? 'border-l' : 'border-r'}`}>#</th>
                                                <th className={`p-3 ${isRTL ? 'border-l' : 'border-r'}`}>{t('persons.fields.contactName', 'Contact Name')}</th>
                                                <th className={`p-3 ${isRTL ? 'border-l' : 'border-r'}`}>{t('persons.fields.position', 'Position')}</th>
                                                <th className={`p-3 ${isRTL ? 'border-l' : 'border-r'}`}>{t('persons.fields.email', 'Official Email')}</th>
                                                <th className="p-3 w-10 text-center text-red-400"><Trash2 size={12}/></th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 text-[11px] font-bold">
                                            {data.contacts.length === 0 ? (
                                                <tr><td colSpan="5" className="p-10 text-center text-gray-300 italic uppercase font-black tracking-widest">{t('persons.messages.noContacts', 'No contacts defined.')}</td></tr>
                                            ) : data.contacts.map((c, idx) => (
                                                <tr key={idx}
                                                    onClick={() => setSelectedContactIndex(idx)}
                                                    className={`cursor-pointer transition-colors ${selectedContactIndex === idx ? 'bg-blue-100/50' : 'hover:bg-blue-50'}`}>
                                                    <td className={`p-3 text-center font-bold text-gray-400 ${isRTL ? 'border-l' : 'border-r'}`}>{idx + 1}</td>
                                                    <td className={`p-1 ${isRTL ? 'border-l' : 'border-r'}`}>
                                                        <input className={`w-full bg-transparent outline-none p-2 font-bold text-blue-900 uppercase ${isRTL ? 'text-right' : 'text-left'}`} value={c.contact_name || ''} onChange={e => updateContact(idx, 'contact_name', e.target.value)} disabled={!isEditable} />
                                                    </td>
                                                    <td className={`p-1 ${isRTL ? 'border-l' : 'border-r'}`}>
                                                        <input className={`w-full bg-transparent outline-none p-2 italic text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`} value={c.position || ''} onChange={e => updateContact(idx, 'position', e.target.value)} disabled={!isEditable} />
                                                    </td>
                                                    <td className={`p-1 ${isRTL ? 'border-l' : 'border-r'}`}>
                                                        <input type="email" className={`w-full bg-transparent outline-none p-2 text-blue-600 lowercase ${isRTL ? 'text-right' : 'text-left'}`} value={c.email || ''} onChange={e => updateContact(idx, 'email', e.target.value)} disabled={!isEditable} />
                                                    </td>
                                                    <td className="p-1 text-center">
                                                        <button onClick={(e) => removeContact(idx, e)} disabled={!isEditable} className="p-2 text-gray-300 hover:text-red-600 disabled:opacity-50"><Trash2 size={14} /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Detail Section */}
                            {selectedContactIndex !== null && data.contacts[selectedContactIndex] && (
                                <div className="animate-in slide-in-from-bottom duration-300 shrink-0">
                                    <Section title={`${t('persons.sections.extendedDetails', 'Extended Details')}: ${data.contacts[selectedContactIndex].contact_name || t('persons.sections.newMember', 'New Member')}`} isRTL={isRTL}>
                                        <div className="grid grid-cols-4 gap-x-8 gap-y-6">
                                            <EditableField label={t('persons.fields.phone', 'Work Phone')} value={data.contacts[selectedContactIndex].phone} onChange={v => updateContact(selectedContactIndex, 'phone', v)} disabled={!isEditable} isRTL={isRTL} />
                                            <EditableField label={t('persons.fields.mobile', 'Mobile / WhatsApp')} value={data.contacts[selectedContactIndex].mobile} onChange={v => updateContact(selectedContactIndex, 'mobile', v)} disabled={!isEditable} isRTL={isRTL} />
                                            <EditableField label={t('persons.fields.personalEmail', 'Personal Email')} type="email" value={data.contacts[selectedContactIndex].personal_email} onChange={v => updateContact(selectedContactIndex, 'personal_email', v)} disabled={!isEditable} isRTL={isRTL} />
                                            <EditableField label={t('persons.fields.country', 'Country')} value={data.contacts[selectedContactIndex].country} onSearch={() => handleSearch('country', t('common.search', 'Search'), 'DOMAIN', selectedContactIndex, 'COUNTRY')} disabled={!isEditable} isRTL={isRTL} />
                                            
                                            <div className="col-span-2">
                                                <EditableField label={t('persons.fields.address', 'Full Address')} value={data.contacts[selectedContactIndex].address} onChange={v => updateContact(selectedContactIndex, 'address', v)} disabled={!isEditable} isRTL={isRTL} />
                                            </div>
                                            
                                            <EditableField label={t('persons.fields.city', 'City')} value={data.contacts[selectedContactIndex].city} onChange={v => updateContact(selectedContactIndex, 'city', v)} disabled={!isEditable} isRTL={isRTL} />

                                            <div className={`flex flex-col gap-1 border-b border-gray-200 py-1.5 px-1 focus-within:border-blue-500 transition-all ${isRTL ? 'text-right' : 'text-left'}`}>
                                                <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-widest italic leading-none">{t('persons.fields.primary', 'Primary Contact')}</label>
                                                <div className="pt-1">
                                                    <input type="checkbox" checked={data.contacts[selectedContactIndex].is_primary || false} onChange={e => updateContact(selectedContactIndex, 'is_primary', e.target.checked)} disabled={!isEditable} className="w-4 h-4 accent-blue-600 cursor-pointer"/>
                                                </div>
                                            </div>

                                            <div className="col-span-4 bg-orange-50 p-3 rounded border border-orange-100 flex items-center gap-3 mt-2">
                                                <Info size={16} className="text-orange-500 shrink-0" />
                                                <p className="text-[9px] text-orange-700 font-bold uppercase italic tracking-tighter">
                                                    {t('persons.messages.infoLinked', 'Information linked to selected contact row above.')}
                                                </p>
                                            </div>
                                        </div>
                                    </Section>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {activeTab === 'SYSTEM_ACCESS' && (
                        <div className="h-40 flex flex-col items-center justify-center text-gray-300 italic border-2 border-dashed rounded-lg font-black uppercase opacity-50">
                            <Lock size={48} className="mb-4 animate-pulse"/>
                            <span>{t('persons.messages.accessControlled', 'System Access Controlled Here')}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- المكونات الداخلية ---
const HeaderField = ({ label, value, onChange, width, isBold, disabled, placeholder, isRTL }) => (
    <div className={`flex flex-col px-4 border-gray-200 shrink-0 ${isRTL ? 'border-l text-right last:border-0' : 'border-r text-left last:border-0'}`}>
        <label className="text-[0.8em] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic leading-none">{label}</label>
        <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-blue-900 text-[13px] tracking-tighter' : 'text-blue-700 text-[12px]'} bg-transparent transition-all focus:border-blue-500 placeholder:opacity-30 placeholder:lowercase placeholder:font-normal placeholder:italic ${isRTL ? 'text-right' : 'text-left'}`} 
               value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} placeholder={placeholder} />
    </div>
);

const Section = ({ title, children, isRTL }) => (
    <div className="bg-white border rounded shadow-sm transition-all hover:shadow-md border-gray-200 overflow-hidden h-full">
        <div className={`p-3 bg-slate-50 border-b border-gray-100 font-black text-[#1a3353] uppercase text-[10px] tracking-widest flex items-center gap-2 italic`}>
            <div className="w-1.5 h-4 bg-orange-600 rounded-full shadow-sm"></div> {title}
        </div>
        <div className="p-6">{children}</div>
    </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled, isRTL }) => (
    <div className={`flex flex-col gap-1 border-b border-gray-200 py-1.5 px-1 focus-within:border-blue-500 transition-all ${isRTL ? 'text-right' : 'text-left'}`}>
        <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-widest italic leading-none">{label}</label>
        <div className={`flex items-center justify-between pb-0.5 h-6 `}>
            <input type={type} className={`outline-none font-black text-blue-900 bg-transparent w-full text-[12px] tracking-tighter disabled:opacity-50 ${isRTL ? 'text-right' : 'text-left'}`} 
                   value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
            {onSearch && !disabled && <Search size={14} className="text-blue-300 cursor-pointer hover:text-blue-800 transition-all hover:scale-110 active:scale-90 shrink-0" onClick={onSearch} />}
        </div>
    </div>
);

export default PersonDetails;